import {FIREBASE_CALLABLE_LONG_TIMEOUT_MS, functions, httpsCallable} from "@/firebase/firebaseConfig";
import {currentUser} from "@/firebase/firebaseAuth";
import {normalizeWorkspaceRole, Workspace, WorkspaceMembership, WorkspaceRole, workspaceRoleCanProduce} from "@/lib/types";

const ACTIVE_WORKSPACE_PREFIX = "braiv:activeWorkspaceId:";
const PERSONAL_CONTEXT_SENTINEL = "__personal__";

/**
 * Read the currently-active workspace id from localStorage for the signed-in
 * user. Returns null in the personal context. Mirrors the persistence written
 * by AuthContext so non-React lib calls (uploads, dubbing, translation) can be
 * workspace-aware without threading the id through every call site.
 */
export function getActiveWorkspaceId(): string | null {
    try {
        if (typeof window === "undefined") return null;
        const uid = currentUser()?.uid;
        if (!uid) return null;
        const raw = window.localStorage.getItem(`${ACTIVE_WORKSPACE_PREFIX}${uid}`);
        if (!raw || raw === PERSONAL_CONTEXT_SENTINEL) return null;
        return raw;
    } catch {
        return null;
    }
}

export function membershipCanProduce(role: WorkspaceRole | unknown | null | undefined): boolean {
    if (!role) return false;
    return workspaceRoleCanProduce(role);
}

export function membershipCanManage(role: WorkspaceRole | unknown | null | undefined): boolean {
    return normalizeWorkspaceRole(role) === "owner";
}

export interface WorkspaceMemberView {
    uid: string;
    role: WorkspaceRole;
    status: string;
    displayName: string | null;
    email: string | null;
    photoURL: string | null;
}

export interface WorkspaceInviteView {
    id: string;
    email: string;
    role: WorkspaceRole;
    status: string;
    lastEmailQueued?: boolean;
    lastAcceptUrl?: string;
    /** Invite expiry as epoch milliseconds, or null if not set. */
    expiresAt?: number | null;
}

/**
 * Create a team workspace. Rollout admin-gated server-side.
 */
export function createWorkspace(name: string): Promise<Workspace> {
    const fn = httpsCallable(functions, "workspace-createWorkspace");
    return fn({name}).then((result) => result.data as Workspace);
}

/**
 * Rename a workspace. Owner + rollout admin-gated server-side.
 */
export function updateWorkspace(workspaceId: string, name: string): Promise<Workspace> {
    const fn = httpsCallable(functions, "workspace-updateWorkspace");
    return fn({workspaceId, name}).then((result) => result.data as Workspace);
}

/**
 * Replace a workspace's favourite source languages. Owner-gated server-side.
 */
export function updateWorkspaceFavoriteSourceLanguages(
    workspaceId: string,
    favoriteSourceLanguages: string[],
): Promise<Workspace> {
    const fn = httpsCallable(functions, "workspace-updateFavoriteSourceLanguages");
    return fn({workspaceId, favoriteSourceLanguages}).then((result) => result.data as Workspace);
}

/**
 * List the current user's active workspace memberships (with subscription state).
 */
export function listMyWorkspaces(): Promise<WorkspaceMembership[]> {
    const fn = httpsCallable(functions, "workspace-listMyWorkspaces", {
        timeout: FIREBASE_CALLABLE_LONG_TIMEOUT_MS,
    });
    return fn({}).then((result) => {
        const memberships = (result.data as WorkspaceMembership[]) || [];
        return memberships.map((membership) => ({
            ...membership,
            role: normalizeWorkspaceRole(membership.role),
        }));
    });
}

/**
 * Get members + pending invites for a workspace.
 */
export function getWorkspaceMembers(workspaceId: string): Promise<{
    members: WorkspaceMemberView[];
    invites: WorkspaceInviteView[];
    contributorSeatQuantity: number;
    assignedContributorSeats: number;
}> {
    const fn = httpsCallable(functions, "workspace-getWorkspaceMembers");
    return fn({workspaceId}).then((result) => {
        const data = result.data as {
            members: WorkspaceMemberView[];
            invites: WorkspaceInviteView[];
            contributorSeatQuantity?: number;
            assignedContributorSeats?: number;
        };
        return {
            members: (data.members || []).map((member) => ({
                ...member,
                role: normalizeWorkspaceRole(member.role),
            })),
            invites: (data.invites || []).map((invite) => ({
                ...invite,
                role: normalizeWorkspaceRole(invite.role),
            })),
            contributorSeatQuantity: Number(data.contributorSeatQuantity || 0) || 0,
            assignedContributorSeats: Number(data.assignedContributorSeats || 0) || 0,
        };
    });
}

/**
 * Owner sets paid Contributor seats on the workspace subscription (card on file).
 * Matches Starter pricing with PPP.
 */
export function setContributorSeatQuantity(
    workspaceId: string,
    quantity: number,
): Promise<{
    success: boolean;
    contributorSeatQuantity: number;
    assignedContributorSeats: number;
    freeSeats: number;
}> {
    const fn = httpsCallable(functions, "workspace-setContributorSeatQuantity");
    return fn({workspaceId, quantity}).then((result) => result.data as {
        success: boolean;
        contributorSeatQuantity: number;
        assignedContributorSeats: number;
        freeSeats: number;
    });
}

/**
 * Invite a teammate by email. Owner + rollout admin-gated server-side.
 */
export function inviteToWorkspace(
    workspaceId: string,
    email: string,
    role: WorkspaceRole = "member",
): Promise<{success: boolean; inviteId: string; acceptUrl: string; emailQueued?: boolean}> {
    const fn = httpsCallable(functions, "workspace-inviteToWorkspace");
    return fn({workspaceId, email, role: normalizeWorkspaceRole(role)}).then((result) => result.data as {
        success: boolean;
        inviteId: string;
        acceptUrl: string;
        emailQueued?: boolean;
    });
}

/**
 * Accept a pending workspace invite. Not admin-gated.
 */
export function acceptWorkspaceInvite(inviteId: string): Promise<{success: boolean; workspaceId: string}> {
    const fn = httpsCallable(functions, "workspace-acceptWorkspaceInvite");
    return fn({inviteId}).then((result) => result.data as {success: boolean; workspaceId: string});
}

/**
 * Resend a pending invite. Reuses inviteToWorkspace, which refreshes the invite
 * (same deterministic id), bumps its expiry/send count, and re-sends the email.
 */
export function resendWorkspaceInvite(
    workspaceId: string,
    email: string,
    role: WorkspaceRole = "member",
): ReturnType<typeof inviteToWorkspace> {
    return inviteToWorkspace(workspaceId, email, role);
}

/**
 * Change an active member's role. Owner + rollout admin-gated server-side.
 */
export function updateWorkspaceMemberRole(
    workspaceId: string,
    targetUid: string,
    role: WorkspaceRole,
): Promise<{success: boolean; role: WorkspaceRole}> {
    const fn = httpsCallable(functions, "workspace-updateMemberRole");
    return fn({workspaceId, targetUid, role: normalizeWorkspaceRole(role)}).then((result) => result.data as {
        success: boolean;
        role: WorkspaceRole;
    });
}

/**
 * Cancel (revoke) a pending invite. Owner + rollout admin-gated server-side.
 * Invalidates any previously-shared accept link for that invite.
 */
export function revokeWorkspaceInvite(workspaceId: string, inviteId: string): Promise<{success: boolean}> {
    const fn = httpsCallable(functions, "workspace-revokeWorkspaceInvite");
    return fn({workspaceId, inviteId}).then((result) => result.data as {success: boolean});
}

/**
 * Remove a member from a workspace. Owner + rollout admin-gated.
 */
export function removeWorkspaceMember(workspaceId: string, targetUid: string): Promise<{success: boolean}> {
    const fn = httpsCallable(functions, "workspace-removeWorkspaceMember");
    return fn({workspaceId, targetUid}).then((result) => result.data as {success: boolean});
}

/**
 * Leave a workspace. Any member may leave (last owner cannot).
 */
export function leaveWorkspace(workspaceId: string): Promise<{success: boolean}> {
    const fn = httpsCallable(functions, "workspace-leaveWorkspace");
    return fn({workspaceId}).then((result) => result.data as {success: boolean});
}

/**
 * Permanently delete a workspace. Owner + rollout admin-gated server-side.
 */
export function deleteWorkspace(workspaceId: string): Promise<{success: boolean}> {
    const fn = httpsCallable(functions, "workspace-deleteWorkspace");
    return fn({workspaceId}).then((result) => result.data as {success: boolean});
}

export interface WorkspaceCheckoutSessionResult {
    sessionId: string;
    clientSecret: string;
    priceId: string;
    productId: string;
    amount: number;
    currency: string;
    interval: string;
    pppTier: number;
}

/**
 * Create an embedded Stripe Checkout session to subscribe a workspace.
 */
export function createWorkspaceCheckoutSession(params: {
    workspaceId: string;
    tier: "starter" | "pro" | "business";
    billingCycle: "month" | "year";
    coupon?: string;
    country?: string;
}): Promise<WorkspaceCheckoutSessionResult> {
    const fn = httpsCallable(functions, "workspace-createWorkspaceCheckoutSession", {
        timeout: FIREBASE_CALLABLE_LONG_TIMEOUT_MS,
    });
    return fn(params).then((result) => result.data as WorkspaceCheckoutSessionResult);
}

export type ConvertPersonalLicenseMode = "migrate" | "separate";

export interface ConvertPersonalToWorkspaceResult {
    workspaceId: string;
    alreadyConverted: boolean;
    movedCount: number;
    skippedCount: number;
    foldersMovedCount: number;
    integrationsMovedCount?: number;
    standaloneThumbnailsMovedCount?: number;
    brandingTemplatesMovedCount?: number;
    charactersMovedCount?: number;
    brandVoicesMovedCount?: number;
    brandGlossariesMovedCount?: number;
    voiceClonesMovedCount?: number;
    results: Array<{
        videoId: string;
        status: "moved" | "skipped";
        reasonCode?: string;
        message?: string;
    }>;
    licenseMode: ConvertPersonalLicenseMode;
    needsWorkspaceCheckout: boolean;
    creditsMoved: number;
    overageMoved?: number;
    skippedByReason?: Record<string, number>;
    convertedAsParent?: true;
    personalCleared?: boolean;
    warnings?: string[];
}

/**
 * P1 invisible convert: create a home/parent workspace, optionally migrate
 * personal Stripe license, stamp folders, and Case A move selected (or all
 * eligible) personal videos.
 */
export function convertPersonalToWorkspace(params: {
    name: string;
    videoIds: string[] | "all_eligible";
    licenseMode: ConvertPersonalLicenseMode;
}): Promise<ConvertPersonalToWorkspaceResult> {
    const fn = httpsCallable(functions, "workspace-convertPersonalToWorkspace", {
        timeout: FIREBASE_CALLABLE_LONG_TIMEOUT_MS,
    });
    return fn(params).then((result) => result.data as ConvertPersonalToWorkspaceResult);
}

export interface RepairHomeBrandingAssetsResult {
    workspaceId: string;
    brandingTemplatesMovedCount: number;
    charactersMovedCount: number;
    brandVoicesMovedCount: number;
    brandGlossariesMovedCount: number;
}

/**
 * Stamp leftover personal branding (BrandVoices, player templates, characters,
 * glossaries) into the caller's home workspace. Idempotent; used to heal
 * accounts converted before branding migrate shipped.
 */
export function repairHomeBrandingAssets(): Promise<RepairHomeBrandingAssetsResult> {
    const fn = httpsCallable(functions, "workspace-repairHomeBrandingAssets");
    return fn({}).then((result) => result.data as RepairHomeBrandingAssetsResult);
}
